import React, {
  FunctionComponent,
  useState,
  useEffect,
  Suspense,
  lazy,
} from "react";
import { useSelector } from "react-redux";
import {
  BrowserRouter,
  Switch,
  Route,
  Redirect,
  useHistory,
} from "react-router-dom";
import bustCache from "../../core/services/bustCache";
import storage from "../../core/services/storage";
import PrivateRoute from "../PrivateRoute";
import { RootState } from "../../core/store/types";

const Home = lazy(() => import("../../pages/Home"));
const Login = lazy(() => import("../../pages/Login"));
const Signup = lazy(() => import("../../pages/Signup"));
const ForgotPassword = lazy(() => import("../../pages/ForgotPassword"));
const ResetPassword = lazy(() => import("../../pages/ResetPassword"));
const VerifyAccount = lazy(() => import("../../pages/VerifyAccount"));
const Portfolio = lazy(() => import("../../pages/Portfolio"));
const CoinInfo = lazy(() => import("../../pages/CoinInfo"));
const TradeFlow = lazy(() => import("../../pages/TradeFlow"));
const Market = lazy(() => import("../../pages/Market"));
const Settings = lazy(() => import("../../pages/Settings"));
const Orders = lazy(() => import("../../pages/Orders"));
const Orderbook = lazy(() => import("../../pages/Orderbook"));
const Trades = lazy(() => import("../../pages/Trades"));
const News = lazy(() => import("../../pages/News"));
const NewsDetails = lazy(() => import("../../pages/NewsDetails"));
const ErrorPage = lazy(() => import("../../pages/ErrorPage"));
const PrivacyPage = lazy(() => import("../../pages/Privacy"));

const AppRouter: FunctionComponent = () => {
  const history = useHistory();
  const [forceRefresh, setForceRefresh] = useState<boolean>(false);

  const updateOnlineStatus = () => {
    if (!navigator.onLine) {
      history.push("/offline");
    } else {
      history.goBack();
    }
  };
  const { coinInfo } = useSelector((state: RootState) => state.marketReducer);

  useEffect(() => {
    const currentVersion = storage.get("version");
    const latestVersion = process.env.REACT_APP_VERSION;
    if (currentVersion !== latestVersion) {
      bustCache();
      setForceRefresh(true);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);
    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };

    // eslint-disable-next-line
  }, []);

  return (
    <BrowserRouter forceRefresh={forceRefresh}>
      <Suspense fallback={<div />}>
        <Switch>
          <Route
            path="/cms*"
            component={() => {
              window.location.href =
                process.env.NODE_ENV === "development"
                  ? "http://localhost:1337/admin"
                  : `${window.location.origin}/cms/admin`;
              return null;
            }}
          />
          <Route exact path="/" component={Home} />
          <Route exact path="/login" component={Login} />
          <Route exact path="/create-account" component={Signup} />
          <Route exact path="/forgot-password" component={ForgotPassword} />
          <Route exact path="/passwordreset" component={ResetPassword} />
          <Route exact path="/verify" component={VerifyAccount} />
          <Route exact path="/privacy" component={PrivacyPage} />

          <PrivateRoute exact path="/portfolio" component={Portfolio} />
          <PrivateRoute exact path="/transactions" component={Trades} />
          <PrivateRoute exact path="/market" component={Market} />
          <PrivateRoute exact path="/orderbook" component={Orderbook} />
          <PrivateRoute exact path="/settings" component={Settings} />
          {coinInfo ? (
            <>
              <PrivateRoute exact path="/token" component={CoinInfo} />
              <PrivateRoute exact path="/trade" component={TradeFlow} />
              <PrivateRoute exact path="/orders" component={Orders} />
              <PrivateRoute exact path="/news" component={News} />
              <PrivateRoute exact path="/news/:id" component={NewsDetails} />
            </>
          ) : (
            <Redirect to={{ pathname: "/portfolio" }} />
          )}
          <Route
            exact
            path="/(offline|404|unreachable)"
            component={ErrorPage}
          />
          <Route exact path="/*" component={ErrorPage} />
        </Switch>
      </Suspense>
    </BrowserRouter>
  );
};

export default AppRouter;
